import React, { useRef, useState, useEffect, lazy, Suspense } from 'react';
import Hero from '@/components/Hero';
import Header from '@/components/Header';
import MobileWhatsAppCTA from '@/components/MobileWhatsAppCTA';

import { initWhatsAppSettings } from '@/utils/whatsapp/whatsAppSettings';

// Lazy-load all below-the-fold sections
const Benefits = lazy(() => import('@/components/Benefits'));
const PainSection = lazy(() => import('@/components/PainSection'));
const SolutionSection = lazy(() => import('@/components/SolutionSection'));
const Testimonials = lazy(() => import('@/components/Testimonials'));
const AboutTaxHealth = lazy(() => import('@/components/AboutTaxHealth'));
const RestitutionSection = lazy(() => import('@/components/RestitutionSection'));
const SavingsComparison = lazy(() => import('@/components/SavingsComparison'));
const TargetAudience = lazy(() => import('@/components/TargetAudience'));
const ServicesSection = lazy(() => import('@/components/ServicesSection'));
const ProcessSection = lazy(() => import('@/components/ProcessSection'));
const FAQ = lazy(() => import('@/components/FAQ'));
const FinalCTA = lazy(() => import('@/components/FinalCTA'));
const Footer = lazy(() => import('@/components/Footer'));

const LazySection: React.FC<{ children: React.ReactNode; minHeight?: string }> = ({ children, minHeight = '200px' }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref}>
      {visible ? (
        <Suspense fallback={<div style={{ minHeight }} />}>
          {children}
        </Suspense>
      ) : (
        <div style={{ minHeight }} />
      )}
    </div>
  );
};

const Index: React.FC = () => {
  const calculatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initWhatsAppSettings();
  }, []);

  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow pt-14 md:pt-16">
        <Hero scrollToCalculator={scrollToCalculator} />
        <LazySection><Benefits /></LazySection>
        <LazySection><PainSection /></LazySection>
        <LazySection><SolutionSection /></LazySection>
        <LazySection minHeight="400px"><Testimonials /></LazySection>
        <LazySection><AboutTaxHealth /></LazySection>
        <LazySection><RestitutionSection /></LazySection>
        <LazySection><SavingsComparison /></LazySection>
        <LazySection><TargetAudience /></LazySection>
        <LazySection><ServicesSection /></LazySection>
        <LazySection><ProcessSection /></LazySection>
        <LazySection><FAQ /></LazySection>
        <LazySection><FinalCTA /></LazySection>
      </main>
      
      <LazySection><Footer /></LazySection>
      <MobileWhatsAppCTA />
    </div>
  );
};

export default Index;
